import { Analytics } from 'src/providers/analytics/analytics';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { Auth } from 'src/providers/auth/auth';
import { Client } from 'src/providers/client/client';
import { CookieProvider } from 'src/providers/cookie/cookie';
import { Me } from 'src/providers/me/me';
import { TidySession } from 'src/providers/tidy-session/tidy-session';

import { privateProSignUpDataModel } from 'src/models/private-pro-signup';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import validationUtils from 'src/shared/util/validation-utils';
import { Util } from 'src/shared/util/util';
import { DISPLAY_LANGUAGE_OPTIONS } from 'src/shared/constants/display-languages';
import { Loading } from 'src/shared/components/loading/loading';
import { OnboardingFlow } from 'src/shared/enums/onboarding-flow';
import { WindowService } from 'src/shared/providers/window.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { Subscription } from 'rxjs';
import { CookieService } from 'ngx-cookie';
import { TranslationService } from 'src/tidy-ui-components/providers/translation.service';
import { UtmService } from 'src/providers/utm/utm.service';

@Component({
    templateUrl: 'create-account.html',
    standalone: false
})

export class CreateAccountPage implements OnDestroy, OnInit {

  accountTypes: any;
  customerResponse: any;
  form: UntypedFormGroup;
  flowType: OnboardingFlow;
  giftCode: any = false;
  giftCodeMessage: string;
  loaded: boolean;
  ownerTypeItems: any;
  submitted: boolean;
  termsError: boolean;
  utmzTidy: any;
  alreadyTaken: boolean;
  errorMessage: string;
  proName: string;
  proTeamName: string;
  hkId: number;
  quotes: any;
  referralCode: string;
  languageItems: any;
  paramsSubscription: Subscription;


  boundCreateAccount = this.createAccount.bind(this);

constructor(
    private auth: Auth,
    private client: Client,
    private fb: UntypedFormBuilder,
    private cookieProvider: CookieProvider,
    private me: Me,
    private navCtrl: CustomNavController,
    private route: ActivatedRoute,
    private tidySession: TidySession,
    private util: Util,
    private storage: TidyStorage,
    private cookieService: CookieService,
    private windowService: WindowService,
    private analytics: Analytics,
    private onboardingProvider: OnboardingProvider,
    private translationService: TranslationService,
    private utmService: UtmService
  ) {
    this.form = this.fb.group({
      accountName: [''],
      firstName: ['', Validators.compose([Validators.required, Validators.minLength(2)])],
      lastName: ['', Validators.compose([Validators.required, Validators.minLength(2)])],
      email: ['', [Validators.required, validationUtils.validateEmail]],
      password: ['', Validators.required],
      type: [''],
      terms: [true, Validators.requiredTrue],
      numberOfProperties: [''],
      ownerType: [''],
      language: ['']
    });
    this.storage.clear();
  }

  ngOnInit() {
    // Capture UTMs from URL if present
    this.utmService.captureUtmsFromUrl();
    localStorage.setItem('shouldListenToLanguageChange', 'true');
    this.loaded = false;
    this.initReferral();
    this.flowType = this.route.snapshot.data.flowType;
    this.tidySession.onboardingFlow = this.flowType;
    if (this.flowType === OnboardingFlow.PRIVATE) {
      this.getPrivateParams();
    }
    if (this.flowType === OnboardingFlow.NORMAL || this.flowType === OnboardingFlow.PRIVATE) {
      this.setTypeForm();
    }
    if (this.flowType === OnboardingFlow.NORMAL || this.flowType === OnboardingFlow.RENTAL || this.flowType === OnboardingFlow.COMPANY || this.flowType === OnboardingFlow.MAINTENANCE) {
      this.quotes = this.getQuotes();
    }
    if (this.flowType === OnboardingFlow.RENTAL) {
      this.form.controls.type.setValue('rental');
      this.setFormValidation({value: 'rental'});
    }
    this.ownerTypeItems = this.getOwnerTypeItems();
    this.languageItems = this.getLanguageItems();
    this.form.controls.language.setValue('en');
    this.loaded = true;
  }

  getLanguageItems() {
    return DISPLAY_LANGUAGE_OPTIONS;
  }

  selectLanguage() {
    localStorage.setItem('language', this.form.value.language);
    this.translationService.notifyLanguageChange();
  }

  async initReferral(){
    let giftCodeParam: string;
    this.paramsSubscription = this.route.queryParams.subscribe(params => {
      giftCodeParam = params['gift-code'];
    });
    if (giftCodeParam) {
      try {
        this.giftCode = await this.client.validateGiftCode(giftCodeParam);
        this.cookieService.put('giftCodeParam', giftCodeParam);
        this.giftCodeMessage = `Welcome from ${this.giftCode?.giver_name}!`
      } catch (err) {
        this.errorMessage = `${(err.error && err.error.message) ? err.error.message : err.message} / Please check that you entered the correct URL` ;
      }
    }
  }

  getOwnerTypeItems() {
    return [
      {
        viewValue: 'I own the properties',
        value: 'own'
      },
      {
        viewValue: 'I manage properties for others',
        value: 'manage'
      },
      {
        viewValue: 'I just live in a rental',
        value: 'live_in_rental'
      },
      {
        viewValue: 'Other',
        value: 'other'
      }
    ];
  }

  getPrivateParams() {
    this.hkId = parseInt(this.route.snapshot.paramMap.get('hkId'));
    const proName = this.route.snapshot.paramMap.get('proName');
    this.proName = decodeURI(proName);
    const proTeamName = this.route.snapshot.paramMap.get('proTeamName');
    this.proTeamName = decodeURI(proTeamName);
  }

  setTypeForm() {
    this.form.controls.type.setValidators([Validators.required]);
    this.accountTypes = [
      {viewValue: 'For my home', value: 'regular'},
      {viewValue: 'I manage an Airbnb/rental', value: 'rental'},
      {viewValue: 'For an office', value: 'company'}
    ];
  }

  @Loading('', true)
  async createAccount() {
    const privateProSignUpData: privateProSignUpDataModel = {
      hkId: this.hkId,
      proName: this.proName,
      proTeamName: this.proTeamName
    }
    this.submitted = true;
    this.termsError = false;
    if (!this.form.valid) {
      if (!this.form.controls.terms.valid) {
        this.termsError = true;
      }
      return;
    }
    if (this.form.value.firstName.trim().length < 2 || this.form.value.lastName.trim().length < 2) {
      this.form.patchValue({
        firstName: this.form.value.firstName.trim(),
        lastName: this.form.value.lastName.trim()
      });
      return;
    }
    try {
      await this.createUser(privateProSignUpData);
      await this.updateCustomer();
      await this.client.addMetadata({ key: 'language', value: this.form.value.language });
      localStorage.setItem('shouldListenToLanguageChange', 'false');
      localStorage.setItem('language', this.form.value.language);
      if (this.flowType !== OnboardingFlow.DEVELOPER) {
        await this.saveAccountType();
      }
      if (this.form.controls.type.value === 'rental' && this.form.controls.ownerType.value !== 'live_in_rental' && this.flowType !== OnboardingFlow.PRIVATE) {
        this.flowType = OnboardingFlow.RENTAL;
      }
      if (this.flowType === OnboardingFlow.COMPANY) {
        this.flowType = OnboardingFlow.NORMAL;
      }
      localStorage.setItem('firstName', this.form.value.firstName);
      localStorage.setItem('lastName', this.form.value.lastName);
      localStorage.setItem('email', this.form.value.email);
      const bookingExperiment = this.getBookingExperiment();
      const flagToSave = this.getFlagToSave(bookingExperiment);
      localStorage.setItem('bookingExperiment', flagToSave);
      await this.me.load(true);
      this.analytics.trackUpfluenceSignup({
        customerId: this.customerResponse.id,
        firstName: this.form.value.firstName.trim(),
        lastName: this.form.value.lastName.trim(),
        email: this.form.value.email,
        accountType: this.form.value.type || 'regular'
      });
      if (this.flowType === OnboardingFlow.RENTAL && this.form.controls.ownerType.value === 'live_in_rental') {
        this.flowType = OnboardingFlow.NORMAL;
      }
      if (this.flowType === 'rental') {
        const goalsPayload = await this.getGoalsPayload();
        await this.onboardingProvider.saveChosenGoals(goalsPayload);
        await this.onboardingProvider.getGoalsFromServer();
        const route = await this.storage.retrieve('showOnboarding') ? 'properties' : 'rental-get-started';
        this.navCtrl.navigateForward(route);
      } else {
        this.navCtrl.navigateForward(['add-first-property', this.flowType]);
      }
    } catch (error) {
      localStorage.clear();
      const errorMessage = (error.error && error.error.message) ? error.error.message : error.message;
      this.util.showError(errorMessage, 10000);
      this.setErrorMsg(errorMessage);
    }
  }

  async getGoalsPayload() {
    const goalsToPick = await this.onboardingProvider.getGoalsToPickList();
    const payload = goalsToPick.map((goal) => ({
      user_goal_type_id: goal.id,
      chosen: true
    }));
    return {
      user_goals: payload
    }
  }

  getFlagToSave(flag) {
    if (this.form.value.email.includes('rental-control')) {
      return 'rental-control'
    } else if (this.form.value.email.includes('rental-v1a')) {
      return 'rental-v1a'
    } else if (this.form.value.email.includes('rental-v1b')) {
      return 'rental-v1b'
    } else if (this.form.value.email.includes('rental-v2a')) {
      return 'rental-v2a'
    } else if (this.form.value.email.includes('rental-v2b')) {
      return 'rental-v2b'
    } else if (this.form.value.email.includes('rental-v3')) {
      return 'rental-v3'
    } else if (this.form.value.email.includes('consumer-control')) {
      return 'consumer-control'
    } else if (this.form.value.email.includes('consumer-test1')) {
      return 'consumer-test1'
    } else if (this.form.value.email.includes('consumer-test2')) {
      return 'consumer-test2'
    } else {
      return flag;
    }
  }

  getBookingExperiment(): string {
    const flag = '';
    const idString = this.customerResponse.id.toString();
    const lastDigit = idString[idString.length - 1];
    if (this.form.controls.type.value === 'rental') {
      if (lastDigit === 0 || lastDigit === 1 || lastDigit === 2) {
        return 'rental-control';
      } else if (lastDigit === 3 || lastDigit === 4) {
        return 'rental-v2a';
      } else if (lastDigit === 5 || lastDigit === 6) {
        return 'rental-v2b';
      } else if (lastDigit === 7 || lastDigit === 8 || lastDigit === 9) {
        return 'rental-v3';
      }
    } else {
      if (lastDigit === 0 || lastDigit === 1 || lastDigit === 2 || lastDigit === 3) {
        return 'consumer-control';
      } else if (lastDigit === 4 || lastDigit === 5 || lastDigit === 6) {
        return 'consumer-test1';
      } else if (lastDigit === 7 || lastDigit === 8 || lastDigit === 9) {
        return 'consumer-test2';
      }
    }
    return flag;
  }

  async createUser (privateProSignUpData: privateProSignUpDataModel = null) {
    await this.authLogin('signUp', privateProSignUpData);
    await this.getCustomers();
    this.auth.setSession();
    await this.authLogin('signUpPassword', privateProSignUpData);
  }

  async authLogin(scope, privateProSignUpData: privateProSignUpDataModel = null) {
    const credentials = this.getCredentials();
    await this.auth.login(credentials, scope, privateProSignUpData);
  }

  getCredentials () {
    const utms = this.utmService.getUtmsForAuth();
    return {
      username: this.form.value.email,
      password: this.form.value.password,
      date_added_to_cookie: utms.date_added_to_cookie,
      utm_source: utms.utm_source,
      utm_medium: utms.utm_medium,
      utm_campaign: utms.utm_campaign,
      utm_term: utms.utm_term,
      utm_content: utms.utm_content
    };
  }

  async getCustomers() {
    const customersObj = this.getCustomer();
    this.customerResponse = await this.client.getCustomers(customersObj);
    localStorage.setItem('customer_id', this.customerResponse.id);
    const createdAccountType = this.form.value.ownerType === 'live_in_rental' ? 'regular' : this.form.value.type;
    if (createdAccountType === 'rental') {
      // New rental account → gate booking until a setup call is scheduled
      // (see DemoBlockService.shouldGateNewRental).
      localStorage.setItem('justCreatedAccount', 'true');
    }
    const cartMetadata = {
      customer: {
        booked_via: 'Self Booking',
        email: this.form.value.email,
        first_name: this.form.value.firstName.trim(),
        last_name: this.form.value.lastName.trim(),
        account_type: this.form.value.ownerType === 'live_in_rental' ? 'regular' : this.form.value.type,
        account_name: this.form.value.accountName,
        customer_id: this.customerResponse.id,
        ...this.customerResponse
      }
    }
    await this.storage.save('cartMetadata', cartMetadata);
    await this.storage.save('isFirstBooking', true);
  }

  async updateCustomer() {
    await this.client.updateCustomer({customer: {developer: this.flowType === OnboardingFlow.DEVELOPER}}, this.customerResponse.id);
    if (this.form.value.type === 'rental') {
      const value = [
         {
            question: 'How many rental properties do you manage?',
            answer: this.form.value.numberOfProperties
         },
         {
            question: 'Describe your business:',
            answer: this.form.value.ownerType
         }
      ];
      const metadata = {
         key: 'ca-create-account-page',
         value: JSON.stringify(value)
      }
      await this.client.addMetadata(metadata);
    }
  }

  async saveAccountType() {
    const settings = await this.client.getClientSettings();
    if (this.form.value.ownerType === 'live_in_rental' || this.flowType === OnboardingFlow.MAINTENANCE) {
      settings.profile.account_type = 'regular';
    } else if (this.flowType === OnboardingFlow.NORMAL || this.flowType === OnboardingFlow.PRIVATE) {
      settings.profile.account_type = this.form.value.type;
    } else {
      settings.profile.account_type = this.flowType;
    }
    if (settings.invoices.alt_email === '') {
      settings.invoices.alt_email = null;
    }
    await this.client.saveClientSettings(settings);
  }

  selectType(type) {
    this.setFormValidation(type);
    this.quotes = this.getUpdatedQuotes();
  }

  selectOwnerType(ownerType) {
    if (ownerType.value === 'live_in_rental') {
      // Clear the number of properties field since they just live in one rental
      this.form.controls.numberOfProperties.clearValidators();
      this.form.controls.numberOfProperties.updateValueAndValidity();
      this.form.controls.numberOfProperties.setValue('1');
    } else {
      // For other owner types, require the number of properties field
      this.form.controls.numberOfProperties.setValidators([Validators.required]);
      this.form.controls.numberOfProperties.updateValueAndValidity();
      if (this.form.controls.numberOfProperties.value === '1') {
        this.form.controls.numberOfProperties.setValue('');
      }
    }
  }

  getCustomer() {
    return {
      'customer': {
        'booked_via': 'Self Booking',
        'email': this.form.value.email,
        'first_name': this.form.value.firstName.trim(),
        'last_name': this.form.value.lastName.trim(),
        'password': this.form.value.password,
        'account_type': this.form.value.ownerType === 'live_in_rental' ? 'regular' : this.form.value.type,
        'account_name': this.form.value.accountName,
      }
    }
  }

  openTerms() {
    this.util.openUrl('https://www.tidy.com/terms');
  }

  goToLoginPage() {
    this.navCtrl.navigateForward('login');
  }

  setErrorMsg(error) {
    this.alreadyTaken = (error === 'Email has already been taken');
    this.errorMessage = error;
    setTimeout(() => {
      this.errorMessage = '';
    }, 10000);
  }

  getQuotes() {
    const flowQuotes = {
      [OnboardingFlow.NORMAL]: this.getNormalQuote(),
      [OnboardingFlow.COMPANY]: this.getCompanyQuote(),
      [OnboardingFlow.MAINTENANCE]: this.getMaintenanceQuote(),
      [OnboardingFlow.RENTAL]: this.getRentalQuote()
    };
    return flowQuotes[this.flowType];
  }

  getUpdatedQuotes() {
    const flowQuotes = {
      ['regular']: this.getNormalQuote(),
      ['rental']: this.getRentalQuote(),
      ['company']: this.getCompanyQuote()
    };
    return flowQuotes[this.form.value.type];
  }

  getNormalQuote() {
    return [{
      icon: 'assets/img/tidy_client_1.jpg',
      text: '\"Love my Pro. She remembers (or sees on her app) that I leave sheets in the dryer and put them on the bed for me. TIDY helps me communicate my preferences and make sure its done right.\"',
      person: 'Jenny P'
    },
    {
      icon: 'assets/img/tidy_client_2.jpg',
      text: '\"TIDY, my expectations were high, but you delivered. So nice to come back to a clean home! With maintenance on track! It\'s like the future...\"',
      person: 'Nancy C'
    }];
  }

  getMaintenanceQuote() {
    return [{
      icon: 'assets/img/tidy_office_1.jpg',
      text: '\"TIDY removed the hassle from maintaining my home and helped me become so much more organized!\"',
      person: 'Lisa R'
    },
    {
      icon: 'assets/img/tidy_office_2.jpg',
      text: '\"TIDY let me know what the manufacturers recommend for my smoke detector. I decided to follow it, then realized they weren\'t working at all! Getting suggestions made me sleep better at night.\"',
      person: 'Joanna B'
    }];
  }

  getRentalQuote() {
    return [{
      icon: 'assets/img/randy.jpg',
      text: '\"TIDY integrated into Airbnb for me and now my turnovers are automatic. Even though my guests randomly book, TIDY gets it done for me.\"',
      person: 'Randy Y'
    },
    {
      icon: 'assets/img/jessica.jpg',
      text: '\"Every 90 days or so I need to get my properties cleaned. I use TIDY to get it done, mostly with my own pros, but I have them help me find backups. Works like a charm.\"',
      person: 'Jessica M'
    }];
  }

  getCompanyQuote() {
    return [{
      icon: 'assets/img/tidy_office_1.jpg',
      text: '\"I love how easy it is to schedule maintenance AND cleaning with TIDY!  Our office has never been so consistently clean.\"',
      person: 'Lisa R'
    },
    {
      icon: 'assets/img/tidy_office_2.jpg',
      text: '\"Using TIDY to manage cleaners is so efficient - we schedule them to clean after work so that the office is clean each morning.\"',
      person: 'Joanna B'
    }];
  }

  ngOnDestroy() {
    this.paramsSubscription.unsubscribe();
    localStorage.setItem('shouldListenToLanguageChange', 'false');
  }

  setFormValidation(accountType) {
    if (accountType.value === 'rental') {
      this.form.controls.numberOfProperties.setValidators([Validators.required]);
      this.form.controls.ownerType.setValidators([Validators.required]);
    } else {
      this.form.controls.numberOfProperties.clearValidators();
      this.form.controls.numberOfProperties.updateValueAndValidity();
      this.form.controls.ownerType.clearValidators();
      this.form.controls.ownerType.updateValueAndValidity();
    }
  }
}
